// KPIs › Issues in Founder OS (FOUNDER-OS phase 1; was Portfolio › Issues; 12 "Portfolio
// issues"): the issues the companies escalated to the founder, on the identify → discuss → solve
// board — each card with its company, owner, priority and why; a card opens that company's
// drill-down. Below, the portfolio company's own issues ("Portfolio issues"), worked on the
// ordinary issues board (`?board=own`).
import { AvatarTile, Badge, CountPill, LocationHeader } from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { CircleDotIcon } from "lucide-react";
import type { Api } from "../../api/client";
import type { EscalationItem } from "../../api/types";
import { SwitcherTile } from "../../shell/company-switcher";
import {
  COLUMN_LABELS,
  ISSUE_COLUMNS,
  PRIORITY_BADGE,
  PRIORITY_LABELS,
} from "../cadence/cadence-model";
import { issuesQuery } from "../cadence/cadence-queries";
import type { CompanyAccess } from "../company";
import { LoadError, Loading } from "../screen-parts";
import { relativeDay } from "./portfolio-parts";
import { escalationsQuery } from "./portfolio-queries";

export function PortfolioIssuesScreen({ api, company }: { api: Api; company: CompanyAccess }) {
  const escalated = useQuery(escalationsQuery(api, company.id));
  const own = useQuery(issuesQuery(api, company.id));
  const items = escalated.data?.items ?? [];
  return (
    <div className="flex min-w-0 flex-1 flex-col" data-slot="portfolio-issues">
      <LocationHeader
        icon={CircleDotIcon}
        crumbs={[
          {
            label: "KPIs",
            render: <Link to="/c/$slug/kpis" params={{ slug: company.slug }} />,
          },
        ]}
        title="Issues"
        count={escalated.data ? items.length : undefined}
      />
      <div className="flex min-w-0 flex-col gap-6 px-6 py-5 max-md:px-4">
        <section aria-labelledby="escalated-board" className="flex flex-col gap-3">
          <div>
            <h2 id="escalated-board" className="font-heading text-base font-semibold">
              Escalated to you
            </h2>
            <p className="text-sm text-muted-foreground">
              Raised in a company and lifted to the portfolio by its head or a manager.
            </p>
          </div>
          {escalated.isPending ? (
            <Loading label="Loading the escalated issues" />
          ) : escalated.isError ? (
            <LoadError what="The escalated issues" error={escalated.error} />
          ) : (
            <div className="grid grid-cols-3 gap-4 max-lg:grid-cols-1">
              {ISSUE_COLUMNS.map((col) => {
                const list = items.filter((i) => i.status === col);
                return (
                  <section
                    key={col}
                    aria-label={COLUMN_LABELS[col]}
                    data-column={col}
                    className="flex min-w-0 flex-col gap-2 rounded-lg bg-sunken p-2.5"
                  >
                    <h3 className="flex items-center gap-2 px-1 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                      {COLUMN_LABELS[col]}
                      <CountPill count={list.length} />
                    </h3>
                    {list.length === 0 ? (
                      <p className="px-1 py-3 text-xs text-faint">Nothing here.</p>
                    ) : (
                      list.map((i) => (
                        <EscalatedCard key={i.issue_id} item={i} slug={company.slug} />
                      ))
                    )}
                  </section>
                );
              })}
            </div>
          )}
        </section>
        <section aria-labelledby="portfolio-own" className="flex flex-col gap-3">
          <div className="flex flex-wrap items-end justify-between gap-2">
            <div>
              <h2 id="portfolio-own" className="font-heading text-base font-semibold">
                Portfolio issues
              </h2>
              <p className="text-sm text-muted-foreground">
                Raised at the portfolio level, for the portfolio meeting.
              </p>
            </div>
            <Link
              to="/c/$slug/kpis/issues"
              params={{ slug: company.slug }}
              search={{ board: "own" } as never}
              className="text-sm font-medium text-primary-text hover:underline"
            >
              Open the issues board
            </Link>
          </div>
          {own.isPending ? (
            <Loading label="Loading the portfolio issues" rows={2} />
          ) : own.isError ? (
            <LoadError what="The portfolio issues" error={own.error} />
          ) : own.data.data.length === 0 ? (
            <p className="text-sm text-muted-foreground">No portfolio issue yet.</p>
          ) : (
            <ul className="flex flex-col divide-y divide-border-subtle rounded-md border border-border">
              {own.data.data.map((i) => (
                <li key={i.id} className="flex items-center gap-2 px-3 py-2 text-sm">
                  <Badge variant={PRIORITY_BADGE[i.priority] ?? "neutral"}>
                    {PRIORITY_LABELS[i.priority] ?? i.priority}
                  </Badge>
                  <span className="min-w-0 flex-1 truncate font-medium">{i.title}</span>
                  <span className="text-xs text-muted-foreground">{i.status}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

function EscalatedCard({ item, slug }: { item: EscalationItem; slug: string }) {
  const owner = item.owner_name ?? item.owner_title;
  return (
    <Link
      to="/c/$slug/portfolio/companies/$companyId"
      params={{ slug, companyId: item.company_id }}
      data-slot="escalated-card"
      aria-label={`${item.title} (${item.company_name})`}
      className="flex flex-col gap-1.5 rounded-md border border-border bg-card p-2.5 shadow-xs hover:border-strong focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
    >
      <span className="flex items-center gap-1.5">
        <SwitcherTile name={item.company_name} portfolio={false} />
        <span className="truncate text-xs font-medium text-secondary">{item.company_name}</span>
        <Badge className="ml-auto" variant={PRIORITY_BADGE[item.priority] ?? "neutral"}>
          {PRIORITY_LABELS[item.priority] ?? item.priority}
        </Badge>
      </span>
      <span className="text-sm font-medium text-foreground">{item.title}</span>
      {item.why ? (
        <span className="line-clamp-2 text-xs text-muted-foreground">{item.why}</span>
      ) : null}
      <span className="flex items-center gap-1.5 text-xs text-faint">
        {owner ? (
          <>
            <AvatarTile name={owner} kind="person" size="xs" />
            <span className="truncate text-muted-foreground">{owner}</span>
          </>
        ) : null}
        {item.escalated_at ? (
          <span className="ml-auto">escalated {relativeDay(item.escalated_at)}</span>
        ) : null}
      </span>
    </Link>
  );
}
